/**
 * Paragraph-aligned diff (ENGINE-SPEC §9.8): paragraphs are aligned with a Myers diff, and a changed paragraph pair is then
 * diffed token by token (Myers again). Every operation carries the text it covers, so `before` and `after` are recovered exactly
 * by joining the `equal` and `delete` (resp. `equal` and `insert`) operations.
 */

export type DiffKind = 'equal' | 'delete' | 'insert';

export interface DiffOp {
	kind: DiffKind;
	text: string;
}

export interface DiffRun {
	kind: DiffKind;
	aStart: number;
	aEnd: number;
	bStart: number;
	bEnd: number;
}

/** Myers O(ND) shortest edit script over two sequences, as runs of equal / deleted / inserted elements. */
export function myers<T>(
	a: readonly T[],
	b: readonly T[],
	eq: (x: T, y: T) => boolean = (x, y) => x === y
): DiffRun[] {
	const n = a.length;
	const m = b.length;
	const max = n + m;
	const off = max + 1;
	const v = new Int32Array(2 * max + 3);
	// snapshots of v[-d … d] taken before round d (index k + d)
	const trace: Int32Array[] = [];
	let dEnd = 0;
	outer: for (let d = 0; d <= max; d++) {
		trace.push(v.slice(off - d, off + d + 1));
		for (let k = -d; k <= d; k += 2) {
			let x: number;
			if (k === -d || (k !== d && (v[off + k - 1] as number) < (v[off + k + 1] as number)))
				x = v[off + k + 1] as number;
			else x = (v[off + k - 1] as number) + 1;
			let y = x - k;
			while (x < n && y < m && eq(a[x] as T, b[y] as T)) {
				x++;
				y++;
			}
			v[off + k] = x;
			if (x >= n && y >= m) {
				dEnd = d;
				break outer;
			}
		}
	}
	const ops: DiffRun[] = [];
	let x = n;
	let y = m;
	for (let d = dEnd; d > 0; d--) {
		const vd = trace[d] as Int32Array;
		const at = (k: number) => vd[k + d] as number;
		const k = x - y;
		const prevK = k === -d || (k !== d && at(k - 1) < at(k + 1)) ? k + 1 : k - 1;
		const prevX = at(prevK);
		const prevY = prevX - prevK;
		while (x > prevX && y > prevY) {
			ops.push({ kind: 'equal', aStart: x - 1, aEnd: x, bStart: y - 1, bEnd: y });
			x--;
			y--;
		}
		if (x === prevX) ops.push({ kind: 'insert', aStart: x, aEnd: x, bStart: prevY, bEnd: y });
		else ops.push({ kind: 'delete', aStart: prevX, aEnd: x, bStart: y, bEnd: y });
		x = prevX;
		y = prevY;
	}
	while (x > 0 && y > 0) {
		ops.push({ kind: 'equal', aStart: x - 1, aEnd: x, bStart: y - 1, bEnd: y });
		x--;
		y--;
	}
	ops.reverse();
	const merged: DiffRun[] = [];
	for (const o of ops) {
		const last = merged[merged.length - 1];
		if (last && last.kind === o.kind && last.aEnd === o.aStart && last.bEnd === o.bStart) {
			last.aEnd = o.aEnd;
			last.bEnd = o.bEnd;
		} else merged.push({ ...o });
	}
	return merged;
}

const TOKEN = /\s+|[\p{L}\p{M}\p{N}]+(?:['’][\p{L}\p{M}]+)*|[^\s\p{L}\p{M}\p{N}]/gu;

/** Tokens that concatenate back to the text (words, single punctuation marks, whitespace runs). */
export function tokenize(text: string): string[] {
	return text.match(TOKEN) ?? [];
}

/** Above this many tokens on both sides a paragraph pair is shown as one delete and one insert. */
export const TOKEN_DIFF_LIMIT = 3000;

/** Token-level Myers diff of two texts. */
export function diffTokens(before: string, after: string): DiffOp[] {
	if (before === after) return before === '' ? [] : [{ kind: 'equal', text: before }];
	const a = tokenize(before);
	const b = tokenize(after);
	if (a.length + b.length > TOKEN_DIFF_LIMIT) {
		const ops: DiffOp[] = [];
		if (before) ops.push({ kind: 'delete', text: before });
		if (after) ops.push({ kind: 'insert', text: after });
		return ops;
	}
	const out: DiffOp[] = [];
	for (const o of myers(a, b)) {
		if (o.kind === 'equal') out.push({ kind: 'equal', text: a.slice(o.aStart, o.aEnd).join('') });
		else if (o.kind === 'delete')
			out.push({ kind: 'delete', text: a.slice(o.aStart, o.aEnd).join('') });
		else out.push({ kind: 'insert', text: b.slice(o.bStart, o.bEnd).join('') });
	}
	return out;
}

export interface ParagraphDiff {
	kind: 'same' | 'changed' | 'removed' | 'added';
	before?: string;
	after?: string;
	/** Token-level operations for `changed` (and the whole paragraph for `removed` / `added`). */
	ops: DiffOp[];
}

const splitParagraphs = (s: string): string[] => s.split(/\n[ \t]*\n/);

/** Paragraph-aligned diff: paragraphs by Myers, then changed pairs by tokens (an unequal hunk pairs paragraphs in order). */
export function diffParagraphs(before: string, after: string): ParagraphDiff[] {
	const a = splitParagraphs(before);
	const b = splitParagraphs(after);
	const out: ParagraphDiff[] = [];
	const runs = myers(a, b);
	for (let i = 0; i < runs.length; i++) {
		const r = runs[i] as (typeof runs)[number];
		if (r.kind === 'equal') {
			for (let k = r.aStart; k < r.aEnd; k++)
				out.push({
					kind: 'same',
					before: a[k],
					after: a[k],
					ops: [{ kind: 'equal', text: a[k] as string }],
				});
			continue;
		}
		// a hunk: the deletes and inserts up to the next equal run
		let del: string[] = [];
		let ins: string[] = [];
		let j = i;
		for (; j < runs.length && (runs[j] as DiffRun).kind !== 'equal'; j++) {
			const h = runs[j] as DiffRun;
			if (h.kind === 'delete') del = del.concat(a.slice(h.aStart, h.aEnd));
			else ins = ins.concat(b.slice(h.bStart, h.bEnd));
		}
		i = j - 1;
		const pairs = Math.min(del.length, ins.length);
		for (let k = 0; k < pairs; k++)
			out.push({
				kind: 'changed',
				before: del[k],
				after: ins[k],
				ops: diffTokens(del[k] as string, ins[k] as string),
			});
		for (let k = pairs; k < del.length; k++)
			out.push({
				kind: 'removed',
				before: del[k],
				ops: [{ kind: 'delete', text: del[k] as string }],
			});
		for (let k = pairs; k < ins.length; k++)
			out.push({ kind: 'added', after: ins[k], ops: [{ kind: 'insert', text: ins[k] as string }] });
	}
	return out;
}

/** Rebuild one side of a diff from its operations. */
export function sideOf(ops: readonly DiffOp[], side: 'before' | 'after'): string {
	return ops
		.filter((o) => o.kind === 'equal' || o.kind === (side === 'before' ? 'delete' : 'insert'))
		.map((o) => o.text)
		.join('');
}
